<template>
  <div class="min-h-screen bg-gray-50" id="app">
    <MaintenanceBanner v-if="auth.isAuthenticated" />
    <VersionUpdateBanner v-if="auth.isAuthenticated" />
    <!-- Navigation -->
    <nav v-if="auth.isAuthenticated" class="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50 no-print">
      <div class="container mx-auto flex justify-between items-center">
        <div class="flex items-center gap-4 overflow-hidden">
          <h1 class="font-bold text-xl whitespace-nowrap">KonfPlan</h1>

          <!-- EVENT CONTEXT UI -->
          <div v-if="eventContext.selectedEvent" class="flex items-center gap-3 border-l border-indigo-500 pl-4 animate-fade-in overflow-hidden">
            <a v-if="eventContext.selectedEvent.logo_link" :href="eventContext.selectedEvent.logo_link" target="_blank" rel="noopener noreferrer" class="shrink-0 hover:opacity-80 transition-opacity">
              <EventLogo :src="eventContext.selectedEvent.logo" :alt="eventContext.selectedEvent.name" img-class="h-8 w-auto object-contain bg-white/10 rounded p-1" />
            </a>
            <div class="flex flex-col min-w-0">
              <span class="font-black text-xs uppercase tracking-tighter truncate">{{ eventContext.selectedEvent.name }}</span>
              <span class="text-[10px] text-indigo-200 font-bold whitespace-nowrap">{{ formatZeitraum(eventContext.selectedEvent) }}</span>
              <span v-if="eventContext.selectedEvent.organisatorNamen?.length" class="text-[10px] text-indigo-200 truncate">
                {{ eventContext.selectedEvent.organisatorNamen.join(', ') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Toggle -->
        <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden">
          <MenuIcon />
        </button>

        <!-- Desktop Menu -->
        <div class="hidden md:flex gap-6 items-center relative">
          <select v-if="roleOptions.length > 1" :value="auth.activeRole" @change="handleRoleChange($event.target.value)"
                  class="bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-black uppercase rounded px-2 py-1.5 border border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer"
                  aria-label="Aktive Rolle wechseln">
            <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
          <button @click="toggleInfoPanel" class="hover:underline text-sm font-bold">Info</button>
          <button @click="toggleMessageBoxPanel" class="relative hover:opacity-80" title="Nachrichten" aria-label="Nachrichten">
            <BellIcon class="w-5 h-5"/>
            <span v-if="messageBox.unreadCount > 0" class="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center">{{ messageBox.unreadCount }}</span>
          </button>
          <button @click="handleLogoutClick" class="bg-indigo-700 hover:bg-indigo-800 px-3 py-1 rounded text-xs font-black transition-colors uppercase">Logout</button>
          <InfoPanel v-if="infoPanelOpen" class="absolute right-0 top-full mt-2" @close="infoPanelOpen = false" @open-feedback="openFeedbackModal" />
          <MessageBoxPanel v-if="messageBoxPanelOpen" class="absolute right-0 top-full mt-2" @close="messageBoxPanelOpen = false" />
        </div>
      </div>

      <!-- Mobile Menu Content -->
      <div v-if="mobileMenuOpen" class="md:hidden mt-4 flex flex-col gap-2 pb-2 relative">
        <select v-if="roleOptions.length > 1" :value="auth.activeRole" @change="handleRoleChange($event.target.value)"
                class="bg-indigo-700 text-white text-xs font-black uppercase rounded px-2 py-1.5 border border-indigo-500 focus:outline-none"
                aria-label="Aktive Rolle wechseln">
          <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <button @click="toggleInfoPanel" class="text-left">Info</button>
        <button @click="toggleMessageBoxPanel" class="text-left flex items-center gap-2">
          Nachrichten
          <span v-if="messageBox.unreadCount > 0" class="bg-red-600 text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center">{{ messageBox.unreadCount }}</span>
        </button>
        <button @click="handleLogoutClick" class="text-left text-red-200">Logout</button>
        <InfoPanel v-if="infoPanelOpen" @close="infoPanelOpen = false" @open-feedback="openFeedbackModal" />
        <MessageBoxPanel v-if="messageBoxPanelOpen" @close="messageBoxPanelOpen = false" />
      </div>
    </nav>

    <!-- Content -->
    <main class="container mx-auto p-4">
      <router-view />
    </main>

    <FeedbackModal :is-visible="showFeedbackModal" @close="showFeedbackModal = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore, ROLE_PRIORITY, ROLE_PATHS, ROLE_LABELS } from './stores/auth';
import { useEventContextStore } from './stores/eventContext';
import { useUnsavedChangesStore } from './stores/unsavedChanges';
import { useMessageBoxStore } from './stores/messageBox';
import { useInactivityLogout } from './composables/useInactivityLogout';
import { Menu as MenuIcon, Bell as BellIcon } from '@lucide/vue';
import { formatZeitraum } from './utils/veranstaltungFormat';
import InfoPanel from './components/InfoPanel.vue';
import MessageBoxPanel from './components/MessageBoxPanel.vue';
import EventLogo from './components/EventLogo.vue';
import FeedbackModal from './components/FeedbackModal.vue';
import MaintenanceBanner from './components/MaintenanceBanner.vue';
import VersionUpdateBanner from './components/VersionUpdateBanner.vue';

const POLL_INTERVAL_MS = 60000;

const router = useRouter();
const auth = useAuthStore();
const eventContext = useEventContextStore();
const unsavedChanges = useUnsavedChangesStore();
const messageBox = useMessageBoxStore();
const mobileMenuOpen = ref(false);
const infoPanelOpen = ref(false);
const messageBoxPanelOpen = ref(false);
const showFeedbackModal = ref(false);
let unreadPollInterval = null;

// Rollen-Dropdown im Seitenkopf (siehe #751): nur die Rollen, die der Nutzer tatsächlich hält,
// in stabiler Reihenfolge (ROLE_PRIORITY) statt in der u.U. wechselnden Reihenfolge des
// Token-Arrays.
const roleOptions = computed(() =>
  ROLE_PRIORITY.filter((role) => auth.userRoles.includes(role)).map((role) => ({ value: role, label: ROLE_LABELS[role] }))
);

const handleRoleChange = (role) => {
  auth.setActiveRole(role);
  router.push(ROLE_PATHS[role]);
  mobileMenuOpen.value = false;
};

const toggleInfoPanel = () => {
  infoPanelOpen.value = !infoPanelOpen.value;
};

const toggleMessageBoxPanel = () => {
  messageBoxPanelOpen.value = !messageBoxPanelOpen.value;
};

// Automatischer Inaktivitäts-Logout läuft bewusst NICHT über diesen Handler, sondern ruft
// auth.logout() direkt auf (siehe useInactivityLogout.js) - er darf nie durch einen Dialog
// blockiert werden, den niemand mehr beantwortet.
const handleLogoutClick = () => {
  if (unsavedChanges.hasUnsavedChanges()
      && !window.confirm('Es gibt ungespeicherte Änderungen. Trotzdem abmelden und Änderungen verwerfen?')) {
    return;
  }
  auth.logout();
};

const openFeedbackModal = () => {
  infoPanelOpen.value = false;
  mobileMenuOpen.value = false;
  showFeedbackModal.value = true;
};

useInactivityLogout();

onMounted(() => {
  if (auth.isAuthenticated) {
    messageBox.fetchUnreadCount();
  }
  unreadPollInterval = setInterval(() => {
    if (auth.isAuthenticated) {
      messageBox.fetchUnreadCount();
    }
  }, POLL_INTERVAL_MS);
});

onUnmounted(() => {
  clearInterval(unreadPollInterval);
});
</script>

<style>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
}
</style>
